export const DEFAULT_PHONE_COUNTRY_CODE = '+371';

export const PHONE_COUNTRY_OPTIONS = [
  { code: '+371', label: 'LV (+371)' },
  { code: '+370', label: 'LT (+370)' },
  { code: '+372', label: 'EE (+372)' },
  { code: '+358', label: 'FI (+358)' },
  { code: '+49', label: 'DE (+49)' },
  { code: '+44', label: 'UK (+44)' },
  { code: '+1', label: 'US (+1)' },
];

/** Allowed national (post country code) digit count for a dialling code. */
export interface PhoneNumberRule {
  minDigits: number;
  maxDigits: number;
}

/**
 * E.164 allows up to 15 digits in total including the country code, so this is
 * the fallback for codes we have no national numbering plan for.
 */
const DEFAULT_PHONE_NUMBER_RULE: PhoneNumberRule = {
  minDigits: 6,
  maxDigits: 15,
};

/** National numbering plans we enforce exactly. */
const PHONE_NUMBER_RULES: Record<string, PhoneNumberRule> = {
  // Latvian subscriber numbers are always 8 digits.
  '+371': { minDigits: 8, maxDigits: 8 },
  // Finnish mobile subscriber numbers are 9 digits after the country code.
  '+358': { minDigits: 9, maxDigits: 9 },
};

export const getPhoneNumberRule = (countryCode?: string): PhoneNumberRule =>
  PHONE_NUMBER_RULES[(countryCode || '').trim()] ?? DEFAULT_PHONE_NUMBER_RULE;

/** Strips non-digits and trims to the longest number the country code allows. */
export const clampLocalNumber = (
  localNumber: string,
  countryCode?: string
): string =>
  localNumber
    .replace(/\D/g, '')
    .slice(0, getPhoneNumberRule(countryCode).maxDigits);

/** i18n descriptor for the length a country code expects. */
export interface PhoneLengthMessage {
  key: string;
  defaultValue: string;
  values: { digits: number; min: number; max: number; code: string };
}

export const getPhoneLengthMessage = (
  countryCode?: string
): PhoneLengthMessage => {
  const { minDigits, maxDigits } = getPhoneNumberRule(countryCode);
  const code = (countryCode || DEFAULT_PHONE_COUNTRY_CODE).trim();

  if (minDigits === maxDigits) {
    return {
      key: 'agent.phone.validation.exact_digits.translation',
      defaultValue: '{{code}} numbers must be exactly {{digits}} digits.',
      values: { digits: maxDigits, min: minDigits, max: maxDigits, code },
    };
  }

  return {
    key: 'agent.phone.validation.digit_range.translation',
    defaultValue:
      '{{code}} numbers must be between {{min}} and {{max}} digits.',
    values: { digits: maxDigits, min: minDigits, max: maxDigits, code },
  };
};

export const parsePhoneNumber = (raw?: string | null) => {
  const normalized = (raw || '').trim();
  if (!normalized) return { code: DEFAULT_PHONE_COUNTRY_CODE, local: '' };

  const sortedCodes = PHONE_COUNTRY_OPTIONS.map(option => option.code).sort(
    (a, b) => b.length - a.length
  );
  const matchedCode = sortedCodes.find(code => normalized.startsWith(code));
  if (matchedCode) {
    return {
      code: matchedCode,
      local: normalized.slice(matchedCode.length).replace(/\D/g, ''),
    };
  }

  return {
    code: DEFAULT_PHONE_COUNTRY_CODE,
    local: normalized.replace(/\D/g, ''),
  };
};

export const isValidNotificationLocalNumber = (
  localNumber: string,
  countryCode?: string
) => {
  const digits = localNumber.replace(/\D/g, '');
  const { minDigits, maxDigits } = getPhoneNumberRule(countryCode);
  return digits.length >= minDigits && digits.length <= maxDigits;
};

/** National digits used in Latvian GSM/SMS call-forwarding codes. */
export const toLatvianForwardingNumber = (raw?: string | null): string => {
  const digits = (raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('371') && digits.length >= 11) {
    return digits.slice(3);
  }
  return digits;
};

const isDisplayablePhoneNumber = (value?: string | null): value is string => {
  const normalized = (value || '').trim();
  if (!normalized) return false;
  const lower = normalized.toLowerCase();
  return !lower.startsWith('sip:') && !normalized.includes('@');
};

/** Public number callers use to reach an agent. */
export const getAgentPhoneNumber = (
  agent?: {
    phone_number?: string | null;
    subscription?: { sip_number?: string | null };
  } | null
): string | undefined => {
  const phoneNumber = agent?.phone_number;
  if (isDisplayablePhoneNumber(phoneNumber)) {
    return phoneNumber.trim();
  }
  const sipNumber = agent?.subscription?.sip_number;
  if (isDisplayablePhoneNumber(sipNumber)) {
    return sipNumber.trim();
  }
  return undefined;
};
